import useTranslation from 'next-translate/useTranslation'
import { useQuery } from 'react-query'

import { PondImage } from '@/components/_base/PondImage'
import { ModalGalleryAndPlanimetry } from '@/components/Modal/Types/GalleryAndPlanimetry'
import { ContextValue } from '@/context/Cities'
import { useModal } from '@/context/ModalContext'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { fetchPlaceholderImagesDataURL } from '@/utils/fetch-placeholder-images-data-url'
import { isEnter } from '@/utils/keyboard-helper'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import {
  detailGalleryImageOverlay,
  detailMosaicContainer,
  detailMosaicImage,
  detailMosaicImageButton,
} from './style.css'

export const imageMosaicHeight = 453

type DesktopImageGalleryProps = {
  cities: ContextValue
  citySlug: string
  eventCategory: GTMEventCategory
  handleUnlock?: VoidFunction
  id: string
  photos: ListingCate['photos']
  planimetryUrl: string
  title: string
}

export const DesktopImageGallery: React.FC<DesktopImageGalleryProps> = ({
  cities,
  citySlug,
  eventCategory,
  handleUnlock,
  id,
  photos,
  planimetryUrl,
  title,
}) => {
  const { t } = useTranslation()
  const { closeModal, openModal } = useModal()

  const { data: placeholderImagesDataURL, isLoading } = useQuery(`listing-${id}-placeholder-photos`, () =>
    fetchPlaceholderImagesDataURL(photos.slice(0, 5))
  )

  const isLocked = !!handleUnlock

  const handleGalleryClick =
    (index = 0) =>
    (event: React.SyntheticEvent<HTMLElement>) => {
      if (isLocked) {
        handleUnlock()
        return
      }

      event.preventDefault()
      openModal(
        <ModalGalleryAndPlanimetry
          cities={cities}
          citySlug={citySlug}
          closeModal={closeModal}
          images={photos}
          openType="gallery"
          planimetryUrl={planimetryUrl}
          startIndex={index}
          title={title}
        />
      )

      //TODO: use a unique label with different GTM category [https://casavo.atlassian.net/browse/MT-458?focusedCommentId=22117]
      const eventLabel = { [GTMEventCategory.ListingDetail]: 'Image', [GTMEventCategory.PriorityDetail]: 'Photos' }[
        eventCategory
      ]

      const isHandleClickEnabled =
        eventCategory === GTMEventCategory.ListingDetail || eventCategory === GTMEventCategory.PriorityDetail

      isHandleClickEnabled && handleClick(eventCategory, eventLabel)
    }

  return (
    <div className={detailMosaicContainer}>
      {photos.slice(0, 5).map((element, index) => {
        return (
          <div
            key={element.url}
            className={detailMosaicImage}
            data-testid="mosaic-image-button"
            role="button"
            tabIndex={0}
            onClick={handleGalleryClick(index)}
            onKeyPress={(e) => {
              if (isEnter(e)) {
                handleGalleryClick(index)(e)
              }
            }}
          >
            {!isLoading && (
              <>
                <PondImage
                  alt={index === 0 ? title : undefined}
                  blurDataURL={placeholderImagesDataURL[index] as string}
                  imageOptions={{
                    ar: '1.25:1',
                    blur: isLocked && 100,
                    fit: 'crop',
                    h: imageMosaicHeight / (index === 0 ? 1 : 2),
                  }}
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  src={element.url}
                />

                {index === 4 && (
                  <button
                    className={detailMosaicImageButton}
                    data-testid="mosaic-cta-show-photos"
                    style={{ zIndex: 1 }}
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleGalleryClick(0)(e)
                    }}
                    onKeyPress={(e) => {
                      if (isEnter(e)) {
                        e.stopPropagation()
                        handleGalleryClick(0)(e)
                      }
                    }}
                  >
                    {t('listing-detail:cta_show_photos', {
                      photoLength: photos.length,
                    })}
                  </button>
                )}
              </>
            )}
            {!isLocked && <span className={detailGalleryImageOverlay} />}
          </div>
        )
      })}
    </div>
  )
}
